import React, { useContext, useState, useRef } from 'react';

import { ALERT_TYPE, CUENTA_STATE, MASK_FORMAT, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import { BUSINESS_CODE } from '../../consts/businessCode';
import { ServerRequest } from '../../utils/apiweb';
import { useNav } from '../../components/hooks';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch,
    Select,
    FormLabel
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import CrearCuentaModal from '../../components/controls/CrearCuentaModal';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ManagedContext } from '../../context/custom';
import { useLista } from '../../components/hooks/useLista';
import { usePermissions } from '../../components/hooks/usePermissions';
import { PERMISSIONS } from '../../consts/permissions';
import {TIPS} from "../../consts/tips";
import { parseSerializedIds } from '../../utils/convert';
import { buildFondeaderoEntityInit } from '../FondeaderoView/utils';

function FondeaderosView() {

    // hooks
    const navigate = useNav();
    const { showMessageModal } = useContext(ManagedContext);

    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const [tableVisible, setTableVisible] = useState(false);
    const [searchMessage, setSearchMessage] = useState(null);
    const [createModal, setCreateModal] = useState({ show: false, idTipoFondeadero: 0 });

    const searchRef = useRef();

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.FONDEADERO_EDIT);
    const canReactivar = hasPermission(PERMISSIONS.CUENTA_EDIT);

    // definiciones

    const cellA = () => !canEdit ? '' : (
        <div className='action'>
            <div onClick={() => handleClickFondeaderoAdd()} className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    );

    const cellVMR = (props) => (
        <div className='action'>
            <div onClick={() => handleClickFondeaderoView(props.value)} className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {canEdit && props.row.original.idEstadoCuenta !== CUENTA_STATE.INACTIVA && (
                <>
                    <div onClick={() => handleClickFondeaderoModify(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Modificar">stylus</span>
                    </div>
                    <div onClick={() => handleClickFondeaderoRemove(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Eliminar">delete</span>
                    </div>
                </>
            )}
            {canReactivar && props.row.original.idEstadoCuenta === CUENTA_STATE.INACTIVA && (
                <div onClick={() => handleClickReactivar(props.row.original.idCuenta, props.row.original.numeroCuenta)} className="link">
                    <span className="material-symbols-outlined" title="Reactivar">unarchive</span>
                </div>
            )}
            <div onClick={() => handleClickFondeaderoDataTagger(props.value)} className="link">
                <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    );

    const tableColumns = [
        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '20%' },
        { Header: 'Tipo de fondeadero', accessor: 'tipoFondeadero', width: '30%' },
        { Header: 'Embarcación', accessor: 'embarcacion', width: '20%' },
        {
            Header: 'Estado cuenta',
            Cell: (props) => { return getDescEstadoCuenta(props.value); },
            accessor: 'idEstadoCuenta',
            width: '15%'
        },
        { Header: cellA, Cell: cellVMR, id: 'abm', accessor: 'id', width: '10%', disableGlobalFilter: true, disableSortBy: true }
    ];

    const tabsConfig = [
        {
            key: 'cuenta',
            title: 'Cuenta',
            fields: ['numeroCuenta', 'idPersona', 'idEstadoCuenta', 'etiqueta', 'idsTipoCondicionEspecial', 'idsTipoRecargoDescuento'],
            buildUrl: (filters) => {
                return `/cuenta/filter?` +
                    `numeroCuenta=${encodeURIComponent(filters.numeroCuenta)}&` +
                    `idEstadoCuenta=${filters.idEstadoCuenta}&` +
                    `numeroWeb=&` +
                    `numeroDocumento=&` + // no lo usamos en esta pantalla, es preferible que acceda por persona
                    `idPersona=${filters.idPersona}&` +
                    `etiqueta=${encodeURIComponent(filters.etiqueta)}&` +
                    `idsTipoCondicionEspecial=${encodeURIComponent(parseSerializedIds(filters.idsTipoCondicionEspecial))}&` +
                    `idsTipoRecargoDescuento=${encodeURIComponent(parseSerializedIds(filters.idsTipoRecargoDescuento))}`;
                }
        },
        {
            key: 'datosFondeadero',
            title: 'Datos del fondeadero',
            fields: ['idTipoFondeadero', 'embarcacion'],
            buildUrl: (filters) => {
                return `/datos/filter?` +
                    `idTipoFondeadero=${encodeURIComponent(filters.idTipoFondeadero)}&` +
                    `embarcacion=${encodeURIComponent(filters.embarcacion)}`;
            }
        }
    ];

    // handles
    const handleClickFondeaderoAdd = () => {
        setCreateModal({ show: true, idTipoFondeadero: 0 });
    };

    function CreateFondeadero() {
        setState(prevState => ({ ...prevState, loading: true }));
        setCreateModal(prevState => ({ ...prevState, show: false }));

        const base = buildFondeaderoEntityInit();
        const dataBody = {
            ...base,
            fondeadero: { ...base.fondeadero, idTipoFondeadero: createModal.idTipoFondeadero }
        };

        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    navigate({ to: '/fondeadero/' + OPERATION_MODE.EDIT + '/' + row.fondeadero.id });
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.FONDEADERO,
            null,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const handleClickFondeaderoView = (id) => {
        const url = '/fondeadero/' + OPERATION_MODE.VIEW + '/' + id;
        navigate({ to: url });
    };

    const handleClickFondeaderoModify = (id) => {
        const url = '/fondeadero/' + OPERATION_MODE.EDIT + '/' + id;
        navigate({ to: url });
    };

    const handleClickFondeaderoRemove = (id) => {
        const fondeadero = state.list.find(x => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el número de cuenta ${fondeadero.numeroCuenta}?`,
            onConfirm: () => RemoveFondeadero(id),
        });
    };

    const handleClickReactivar = (idCuenta, numeroCuenta) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de reactivar el número de cuenta ${numeroCuenta}?`,
            onConfirm: () => ReactivarCuenta(idCuenta),
        });
    };

    const handleClickFondeaderoDataTagger = (id) => {
        setState(prevState => {
            return { ...prevState, dataTagger: { showModal: true, idEntidad: id } };
        });
    };

    const [, getRowLista] = useLista({
        listas: ['EstadoCuenta'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'EstadoCuenta',
            timeout: 0
        }
    });

    const getDescEstadoCuenta = (id) => {
        const row = getRowLista('EstadoCuenta', id);
        return (row) ? row.nombre : '';
    };

    // callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return { ...prevState, loading: false };
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return { ...prevState, loading: false };
                });
            });
    };

    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return { ...prevState, loading: false };
        });
    };

    // funciones
    function SearchFondeaderos() {
        const { filters, activeTab } = searchRef.current;
        let resolveSearch;
        const searchDone = new Promise((resolve) => { resolveSearch = resolve; });

        setState(prevState => {
            return { ...prevState, loading: true, list: [] };
        });
        setSearchMessage(null);

        const callbackSuccess = (response) => {
            response.json()
                .then((data) => {
                    setState(prevState => {
                        return { ...prevState, loading: false, list: data };
                    });
                    setTableVisible(true);
                    resolveSearch(true);
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                    resolveSearch(false);
                });
        };

        const activeTabConfig = tabsConfig.find(tab => tab.key === activeTab);
        const paramsUrl = activeTabConfig.buildUrl(filters);

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.FONDEADERO,
            paramsUrl,
            null,
            callbackSuccess,
            (response) => {
                response.clone().json()
                    .then((error) => {
                        if (error?.businessCode === BUSINESS_CODE.MAX_QUERY_SIZE_EXCEEDED) {
                            setState(prevState => {
                                return { ...prevState, loading: false, list: [] };
                            });
                            setTableVisible(false);
                            setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS);
                            return;
                        }
                        callbackNoSuccess(response);
                    })
                    .catch(() => callbackNoSuccess(response));
                resolveSearch(false);
            },
            (error) => {
                callbackError(error);
                resolveSearch(false);
            }
        );

        return searchDone;
    }

    function RemoveFondeadero(id) {
        setState(prevState => {
            return { ...prevState, loading: true };
        });

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                    SearchFondeaderos();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.FONDEADERO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function ReactivarCuenta(idCuenta) {
        setState(prevState => {
            return { ...prevState, loading: true };
        });

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'La cuenta fue reactivada correctamente');
                    SearchFondeaderos();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                });
        };

        const paramsUrl = `/reactivar/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `la embarcación ${dataTaggerRow.embarcacion}` : ''

    return (
        <>
            <Loading visible={state.loading} />

            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de fondeadero"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_FONDEADERO_MODAL}
                    entidad="Fondeadero"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={!canEdit}
                    onConfirm={() => {
                        setState(prevState => {
                            return { ...prevState, dataTagger: { showModal: false, idEntidad: null } };
                        });
                    }}
                    onDismiss={() => {
                        setState(prevState => {
                            return { ...prevState, dataTagger: { showModal: false, idEntidad: null } };
                        });
                    }}
                />
            )}

            {createModal.show && (
                <CrearCuentaModal
                    canConfirm={createModal.idTipoFondeadero > 0}
                    onConfirm={CreateFondeadero}
                    onDismiss={() => setCreateModal({ show: false, idTipoFondeadero: 0 })}
                >
                    <div className="mb-3">
                        <FormLabel htmlFor="idTipoFondeadero" required>Tipo de fondeadero</FormLabel>
                        <Select
                            name="idTipoFondeadero"
                            className="form-control"
                            lista="TipoFondeadero"
                            value={createModal.idTipoFondeadero}
                            onChange={({ target }) => setCreateModal(prevState => ({ ...prevState, idTipoFondeadero: target.value }))}
                        />
                    </div>
                </CrearCuentaModal>
            )}

            <SectionHeading
                titles={[{ title: 'Tributos' }, { title: 'Administración de fondeaderos' }]}
                onAddClick={canEdit ? handleClickFondeaderoAdd : null}
                tableVisible={tableVisible}
            />

            <section className='section-accordion'>
                <WrapperAdvancedSearch
                    ref={searchRef}
                    message={searchMessage}
                    onClear={() => {
                        setState(prevState => {
                            return { ...prevState, list: [] };
                        });
                        setTableVisible(false);
                    }}
                    onSearch={SearchFondeaderos}
                    tabs={tabsConfig}
                    filters={[
                        { title: 'Número de cuenta', field: 'numeroCuenta', type: 'string', mask: MASK_FORMAT.CUENTA, size: 3, searchOnEnter: true },
                        { title: 'Persona', field: 'idPersona', type: 'persona', size: 5 },
                        { title: 'Estado', field: 'idEstadoCuenta', type: 'selectLista', lista: 'EstadoCuenta', presetValue: 2, presetLabel: 'Activa', size: 2 },
                        { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Tipo de fondeadero', field: 'idTipoFondeadero', type: 'list', lista: 'TipoFondeadero', size: 3 },
                        { title: 'Embarcación', field: 'embarcacion', type: 'string', autoComplete: 'off', size: 9, searchOnEnter: true },
                        { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', filter: row => !row.idTipoTributo || row.idTipoTributo === TRIBUTO_TYPE.FONDEADEROS, size: 6 },
                        { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 6 },
                    ]}
                />

                {tableVisible && (
                    <TableCustom
                        className={'TableCustomBase m-top-20'}
                        columns={tableColumns}
                        data={state.list}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default FondeaderosView;

